import { type FC } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import type {
  EstimationModelResponse,
  ProjectWithEstimationResponse,
} from '@/api/schema/data-contracts';
import { ApiEstimationAdditionalFieldType } from '@/api/schema/data-contracts';
import { TextAreaFormField } from '@/components/atoms/TextAreaFormField';
import { ProjectInfoMessages } from '@/components/organism/NewEstimation/ProjectBasics/ProjectInfo/messages';
import { stripHtml } from '@/helpers/estimation';
import { useMessages } from '@/hooks/messages';
import { sortExpressions } from '@/utils/sorting';

import { AdditionalField } from './AdditionalField';
import { metadataDefinitions } from './messages';
import type { AdditionalFieldsFormModel } from './model';

interface MetadataEditationProps {
  control: Control<AdditionalFieldsFormModel>;
  estimationModel: EstimationModelResponse;
  projectWithEstimation: ProjectWithEstimationResponse;
  isReadOnly: boolean;
  projectDescriptionControl: Control<{ projectDescription: string }>;
  onFieldBlur?: () => void;
}

export const MetadataEditation: FC<MetadataEditationProps> = ({
  control,
  estimationModel,
  projectWithEstimation,
  isReadOnly,
  projectDescriptionControl,
  onFieldBlur,
}) => {
  const messages = useMessages(metadataDefinitions);
  const projectInfoMessages = useMessages(ProjectInfoMessages);
  const formModel = useWatch({ control });
  const projectDescriptionValue = useWatch({
    control: projectDescriptionControl,
    name: 'projectDescription',
  });

  const mandatoryFieldsDefinitions = estimationModel.additionalFields.filter(
    (additionalFieldDefinition) => additionalFieldDefinition.isRequired,
  );

  const mandatoryFields = mandatoryFieldsDefinitions
    .sort(sortExpressions.byOrder)
    .map((fieldDefinition) => (
      <div key={fieldDefinition.id}>
        <label>
          {fieldDefinition.name}{' '}
          <small>
            {fieldDefinition.additionalFieldType ===
            ApiEstimationAdditionalFieldType.DURATION_TIME_SPAN
              ? '(DDD.HH.MM)'
              : ''}
          </small>
        </label>
        <AdditionalField
          key={fieldDefinition.id}
          fieldDefinition={fieldDefinition}
          control={control}
          isReadOnly={isReadOnly}
          onBlur={onFieldBlur}
        />
      </div>
    ));

  const optionalFields = estimationModel.additionalFields
    .filter((additionalFieldDefinition) => !additionalFieldDefinition.isRequired)
    .sort(sortExpressions.byOrder)
    .map((fieldDefinition) => (
      <div key={fieldDefinition.id}>
        <label>
          {fieldDefinition.name}{' '}
          <small>
            {fieldDefinition.additionalFieldType ===
            ApiEstimationAdditionalFieldType.DURATION_TIME_SPAN
              ? '(DDD.HH.MM)'
              : ''}
          </small>
        </label>
        <AdditionalField
          key={fieldDefinition.id}
          fieldDefinition={fieldDefinition}
          control={control}
          isReadOnly={isReadOnly}
          onBlur={onFieldBlur}
        />
      </div>
    ));

  const formFields = formModel.fields ?? [];
  const filledMandatoryFields = mandatoryFieldsDefinitions
    .map((fieldDefinition) => {
      // A definition the form model carries no field for counts as unfilled, rather than crashing
      // the tab on the missing entry.
      const formField = formFields.find(
        (formModelField) =>
          formModelField.estimationModelAdditionalFieldDefinitionId === fieldDefinition.id,
      );
      return (
        formField?.value &&
        formField.value !== '' &&
        (fieldDefinition.additionalFieldType !== ApiEstimationAdditionalFieldType.FORMATTED_TEXT ||
          stripHtml(formField.value as string) !== '')
      );
    })
    .filter((isFilled) => isFilled);

  // EWM-1000: Project Description is rendered right below as a mandatory field (required rule and
  // all) but used to count towards neither side of this fraction, so a converted estimation - whose
  // description the conversion fills in - reported "0% of mandatory information has been provided"
  // with the only other mandatory field still empty. Reported from TEST on 2026-08-25: the user saw
  // 0% where 50% was true. Counted from the form control rather than the loaded estimation so the
  // number tracks what is on screen, exactly like the additional fields above.
  const isProjectDescriptionFilled = stripHtml(projectDescriptionValue ?? '').trim() !== '';
  const mandatoryCount = mandatoryFieldsDefinitions.length + 1;
  const filledCount = filledMandatoryFields.length + (isProjectDescriptionFilled ? 1 : 0);

  // Rounded: three mandatory fields used to render "33.333333333333336%".
  const amountPercentage = Math.round((filledCount / mandatoryCount) * 100);

  return (
    <div className='flex max-w-screen-md flex-col space-y-5'>
      <h2>{messages.mandatoryTitle()}</h2>
      {messages.progressSentenceFormattedWithAmountPercentage({
        amountPercentage: amountPercentage.toString(10),
      })}
      {/* Project Description - Hard-coded mandatory field */}
      <div>
        <label htmlFor='projectDescription'>{projectInfoMessages.projectDescriptionLabel()}</label>
        <p className='mt-1 text-xs text-slate-400'>
          {projectInfoMessages.projectDescriptionHelpText()}
        </p>
        {isReadOnly ? (
          <div className='mt-1 text-sm whitespace-pre-wrap'>
            {projectWithEstimation.estimation.projectDescription ||
              'No Project Description available'}
          </div>
        ) : (
          <div className='mt-1'>
            <TextAreaFormField
              control={projectDescriptionControl}
              fieldPath='projectDescription'
              rules={{
                required: true,
              }}
              placeholder={projectInfoMessages.projectDescriptionHelpText()}
              dataTestId='project-description-input'
              onBlur={onFieldBlur}
            />
          </div>
        )}
      </div>
      {/* EWM-999: the advisory permalink the user typed on the convert page. Read-only - it records
          where the estimation came from, and nothing in this tab has ever been able to change it.
          Absent on every estimation not created from an advisory, so it renders only when set. */}
      {projectWithEstimation.estimation.aiAdvisoryDocumentLink && (
        <div>
          <label htmlFor='aiAdvisoryDocumentLink'>{messages.aiAdvisoryDocumentLinkLabel()}</label>
          <div className='mt-1'>
            <a
              id='aiAdvisoryDocumentLink'
              href={projectWithEstimation.estimation.aiAdvisoryDocumentLink}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary text-sm break-all hover:underline dark:text-white'
            >
              {projectWithEstimation.estimation.aiAdvisoryDocumentLink}
            </a>
          </div>
        </div>
      )}
      {mandatoryFields}
      <hr />
      <h2>{messages.optionalTitle()}</h2>
      {optionalFields}
    </div>
  );
};
